import { useEffect, useMemo, useState } from 'react';

import { useAnalytics } from '../useAnalytics';
import type { ScoreAnalytics, ScoreSeriesEntry } from './models/ScoreTypes';

export function useScoreAnalytics(
	endDate: Date,
	seriesEndDates: Date[] = [],
): {
	scoreAnalytics: ScoreAnalytics | null;
	scoreSeries: ScoreSeriesEntry[];
} {
	const { requestScore } = useAnalytics();

	const [scoreAnalytics, setScoreAnalytics] = useState<ScoreAnalytics | null>(null);
	const [scoreSeries, setScoreSeries] = useState<ScoreSeriesEntry[]>([]);

	const endTime = endDate.getTime();

	// Join the history timestamps so the effect only re-runs on a value change,
	// not on every fresh array identity from the parent render.
	const seriesKey = useMemo(
		() => seriesEndDates.map((date) => date.getTime()).join(','),
		[seriesEndDates],
	);

	useEffect(() => {
		let active = true;

		const times = seriesKey ? seriesKey.split(',').map((value) => Number(value)) : [];

		const request = () => {
			requestScore(
				new Date(endTime),
				times.map((time) => new Date(time)),
			)
				.then(({ analytics, series }) => {
					if (!active) return;
					setScoreAnalytics(analytics);
					setScoreSeries(series);
				})
				.catch(() => {
					// Worker restarted mid-request; retry against the new generation.
					if (active) request();
				});
		};

		request();

		return () => {
			active = false;
		};
	}, [requestScore, endTime, seriesKey]);

	return { scoreAnalytics, scoreSeries };
}
